/**
 * Local File Manager - Access local audio files directly from PC
 * Similar to professional DJ apps like transitions.dj
 * Uses File System Access API for folder access
 */

// Check if File System Access API is supported
export const isFileSystemAccessSupported = () => {
  return 'showDirectoryPicker' in window;
};

/**
 * Request access to a local folder
 * @returns {Promise<FileSystemDirectoryHandle|null>}
 */
export async function requestFolderAccess() {
  if (!isFileSystemAccessSupported()) {
    console.warn('File System Access API not supported');
    return null;
  }

  try {
    const dirHandle = await window.showDirectoryPicker({
      mode: 'read',
      startIn: 'music'
    });
    
    // Store the handle for persistence (optional)
    if ('indexedDB' in window) {
      try {
        await storeFolderHandle(dirHandle);
      } catch (e) {
        console.warn('Could not persist folder access:', e);
      }
    }
    
    return dirHandle;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('User cancelled folder selection');
      return null;
    }
    throw error;
  }
}

/**
 * Store folder handle in IndexedDB for persistence
 */
async function storeFolderHandle(handle) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('djnomad_local_files', 1);
    
    request.onerror = () => reject(request.error);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('folders')) {
        db.createObjectStore('folders', { keyPath: 'id' });
      }
    };
    
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction('folders', 'readwrite');
      const store = tx.objectStore('folders');
      
      store.put({ id: 'musicFolder', handle, timestamp: Date.now() });
      
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    };
  });
}

/**
 * Get stored folder handle from IndexedDB
 * @returns {Promise<FileSystemDirectoryHandle|null>}
 */
export async function getStoredFolderHandle() {
  return new Promise((resolve) => {
    const request = indexedDB.open('djnomad_local_files', 1);
    
    request.onerror = () => resolve(null);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('folders')) {
        db.createObjectStore('folders', { keyPath: 'id' });
      }
    };
    
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction('folders', 'readonly');
      const store = tx.objectStore('folders');
      const getRequest = store.get('musicFolder');
      
      getRequest.onsuccess = () => {
        const result = getRequest.result;
        resolve(result?.handle || null);
      };
      
      getRequest.onerror = () => resolve(null);
    };
  });
}

/**
 * Verify permission for stored folder handle
 * @param {FileSystemDirectoryHandle} handle
 * @returns {Promise<boolean>}
 */
export async function verifyPermission(handle) {
  if (!handle) return false;
  
  try {
    const options = { mode: 'read' };
    
    // Check if permission is already granted
    if ((await handle.queryPermission(options)) === 'granted') {
      return true;
    }
    
    // Request permission if needed
    if ((await handle.requestPermission(options)) === 'granted') {
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Permission verification failed:', error);
    return false;
  }
}

/**
 * Scan folder for audio files recursively
 * @param {FileSystemDirectoryHandle} dirHandle
 * @param {string} path - Current path for display
 * @param {number} maxDepth - Maximum recursion depth
 * @returns {Promise<Array<{name: string, path: string, handle: FileSystemFileHandle, size: number}>>}
 */
export async function scanForAudioFiles(dirHandle, path = '', maxDepth = 3) {
  const audioExtensions = ['.mp3', '.wav', '.ogg', '.flac', '.m4a', '.aac', '.webm'];
  const files = [];
  
  if (maxDepth <= 0) return files;
  
  try {
    for await (const entry of dirHandle.values()) {
      const entryPath = path ? `${path}/${entry.name}` : entry.name;
      
      if (entry.kind === 'file') {
        const ext = entry.name.toLowerCase().slice(entry.name.lastIndexOf('.'));
        if (audioExtensions.includes(ext)) {
          try {
            const file = await entry.getFile();
            files.push({
              name: entry.name,
              path: entryPath,
              handle: entry,
              size: file.size,
              type: file.type,
              lastModified: file.lastModified
            });
          } catch (e) {
            console.warn(`Could not access file: ${entryPath}`, e);
          }
        }
      } else if (entry.kind === 'directory') {
        // Recursively scan subdirectories
        const subFiles = await scanForAudioFiles(entry, entryPath, maxDepth - 1);
        files.push(...subFiles);
      }
    }
  } catch (error) {
    console.error('Error scanning folder:', error);
  }
  
  return files;
}

/**
 * Get File object from FileSystemFileHandle
 * @param {FileSystemFileHandle} handle
 * @returns {Promise<File>}
 */
export async function getFileFromHandle(handle) {
  return await handle.getFile();
}

/**
 * Create object URL for local file
 * @param {FileSystemFileHandle} handle
 * @returns {Promise<string>}
 */
export async function createLocalFileUrl(handle) {
  const file = await handle.getFile();
  return URL.createObjectURL(file);
}

/**
 * Extract metadata from audio file using Web Audio API
 * @param {File} file
 * @returns {Promise<{title: string, artist: string, duration: number, bpm: number|null}>}
 */
export async function extractAudioMetadata(file) {
  return new Promise(async (resolve) => {
    const metadata = {
      title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      artist: 'Unknown Artist',
      duration: 0,
      bpm: null
    };
    
    // Try to get duration from audio element
    const audio = new Audio();
    const url = URL.createObjectURL(file);
    
    audio.onloadedmetadata = () => {
      metadata.duration = Math.round(audio.duration);
      URL.revokeObjectURL(url);
      resolve(metadata);
    };
    
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(metadata);
    };
    
    audio.src = url;
  });
}

/**
 * Parse ID3 tags from MP3 file (basic implementation)
 * @param {File} file
 * @returns {Promise<{title?: string, artist?: string, album?: string}>}
 */
export async function parseID3Tags(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const buffer = e.target.result;
      const view = new DataView(buffer);
      
      // Check for ID3v2 header
      if (view.getUint8(0) === 0x49 && // 'I'
          view.getUint8(1) === 0x44 && // 'D'
          view.getUint8(2) === 0x33) { // '3'
        // ID3v2 found - basic parsing
        // This is a simplified implementation
        // For full ID3 parsing, use a library like jsmediatags
        resolve({});
      } else {
        // Check for ID3v1 at end of file
        resolve({});
      }
    };
    
    reader.onerror = () => resolve({});
    
    // Read first 10KB for ID3v2 header
    reader.readAsArrayBuffer(file.slice(0, 10240));
  });
}

/**
 * Local track object for DJ interface
 */
export class LocalTrack {
  constructor(fileInfo) {
    this.id = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.name = fileInfo.name;
    this.title = fileInfo.name.replace(/\.[^/.]+$/, '');
    this.artist = 'Local File';
    this.path = fileInfo.path;
    this.handle = fileInfo.handle;
    this.size = fileInfo.size;
    this.isLocal = true;
    this.bpm = null;
    this.duration = null;
    this.waveform = null;
    this.audioUrl = null;
  }
  
  async load() {
    const file = await this.handle.getFile();
    this.audioUrl = URL.createObjectURL(file);
    
    // Get metadata
    const metadata = await extractAudioMetadata(file);
    this.duration = metadata.duration;
    
    return this;
  }
  
  unload() {
    if (this.audioUrl) {
      URL.revokeObjectURL(this.audioUrl);
      this.audioUrl = null;
    }
  }
}
